/// <amd-module name="Comments/Common/Telemetry" />
import { ICommentArtifactInfo } from "Comments/Common/Contracts";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { traceError } from "VSS/Platform/Trace";

const enum TelemetryConstants {
    Area = "WebAccessComments",

    // Usage scenarios
    AddComment = "AddComment",
    EditComment = "EditComment",
    EditCommentUpdate = "EditCommentUpdate",
    EditCommentCancel = "EditCommentCancel",
    EditCommentKeepDraft = "EditCommentKeepDraft",
    EditCommentCancelNoChange = "EditCommentCancelNoChange",
    DeleteCommentConfirm = "DeleteCommentConfirm",
    DeleteCommentCancel = "DeleteCommentCancel",
    MoreButton = "MoreButton",
    QuoteReply = "QuoteReply",
    ReactionSelectionButton = "ReactionSelectionButton",
    AddCommentReaction = "AddCommentReaction",
    DeleteCommentReaction = "DeleteCommentReaction",
    PageMoreComments = "PageMoreComments",
    LoadComments = "LoadComments"
}

/**
 * Helper for comments UI usage telemetry.
 */
export class UsageTelemetryHelper {
    private _telemetryService: IVssTelemetryService;

    constructor(private _artifactInfo: ICommentArtifactInfo, private _pageContext: IVssPageContext) {
        this._telemetryService = _pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    }

    /**
     * Publish telemetry for adding a comment.
     * @param commentLength Total length of new comment
     * @param numberOfComments Total number of comments
     */
    public publishAddComment(commentLength: number, numberOfComments: number): void {
        this._publish(TelemetryConstants.AddComment, { commentLength, numberOfComments });
    }

    /**
     * Publish telemetry for clicking edit.
     */
    public publishEditComment(): void {
        this._publish(TelemetryConstants.EditComment);
    }

    /**
     * Publish telemetry for successfully editing a comment.
     * @param previousCommentLength Length of the old comment
     * @param newCommentLemgth Length of the new comment
     * @param commentVersion Previous version of the comment
     */
    public publishEditCommentUpdate(previousCommentLength: number, newCommentLength: number, previousCommentVersion: number): void {
        this._publish(TelemetryConstants.EditCommentUpdate, { previousCommentLength, newCommentLength, previousCommentVersion });
    }

    /**
     * Publish telemetry for deleting draft comment.
     * @param isDirty True to indicate there was a change and it was discarded
     */
    public publishEditCommentCancel(isDirty: boolean): void {
        this._publish(isDirty ? TelemetryConstants.EditCommentCancel : TelemetryConstants.EditCommentCancelNoChange);
    }

    /**
     * Publish telemetry for keeping draft and continuing editing.
     */
    public publishEditCommentKeepDraft(): void {
        this._publish(TelemetryConstants.EditCommentKeepDraft);
    }

    /**
     * Publish telemetry for successfully deleting a comment.
     * @param confirmed True to indicate user confirmed the delete, false if they clicked delete then cancelled
     * @param numberOfComments Total number of comments
     */
    public publishDeleteComment(confirmed: boolean, numberOfComments: number): void {
        this._publish(confirmed ? TelemetryConstants.DeleteCommentConfirm : TelemetryConstants.DeleteCommentCancel, { numberOfComments });
    }

    /**
     * Publish telemetry for clicking the more button.
     */
    public publishClickMoreButton(): void {
        this._publish(TelemetryConstants.MoreButton);
    }

    /**
     * Publish telemetry for clicking quote reply.
     * @param fromSelection True if quote is from selecting a substring of a reply instead of full reply without selection
     */
    public publishQuoteReply(fromSelection: boolean): void {
        this._publish(TelemetryConstants.QuoteReply, { fromSelection });
    }

    /**
     * Publish telemetry for clicking the Reactions selection list button.
     */
    public publishClickReactionSelectionButton(): void {
        this._publish(TelemetryConstants.ReactionSelectionButton);
    }

    /**
     * Publish telemetry for adding a reaction on a comment.
     * @param reactionType Reaction type - Like, Dislike etc
     * @param isAddedFromStatusBar Is reaction added from status bar or directly from list
     */
    public publishEngagedReaction(reactionType: string, isAddedFromStatusBar: boolean = false): void {
        this._publish(TelemetryConstants.AddCommentReaction, { reactionType, isAddedFromStatusBar });
    }

    /**
     * Publish telemetry for deleting a reaction on a comment.
     * @param reactionType Reaction type - Like, Dislike etc
     * @param isAddedFromStatusBar Is reaction deleted from status bar or directly from list
     */
    public publishDisEngagedReaction(reactionType: string, isDeletedFromStatusBar: boolean = false): void {
        this._publish(TelemetryConstants.DeleteCommentReaction, { reactionType, isDeletedFromStatusBar });
    }

    /**
     *  Publish telemetry for paging more comments from the comment service
     * @param pagedCount Number of comments that have been paged so far
     * @param totalCount Total number of comments in the page.
     */
    public publishPageMoreComments(pagedCount: number, totalCount: number): void {
        this._publish(TelemetryConstants.PageMoreComments, { pagedCount, totalCount });
    }

    public publishLoadComments(totalCount: number) {
        this._publish(TelemetryConstants.LoadComments, { totalCount });
    }

    /**
     * Publish telemetry for error.
     * @param error Error object or string
     * @param feature - The feature defines a fine grained scope of a single feature.
     *  It should be unique within the area, and the properties should be used to
     *  distinguish how the feature was used.
     * @param properties - An dictionary of name/value pairs with details about the
     *  event. This MAY contain simple or complex values.
     */
    public traceError(feature: string, error: Error | string, properties?: { [key: string]: any }): void {
        traceError(this._pageContext, TelemetryConstants.Area, feature, typeof error === "string" ? error : error.message, properties);
    }

    private _publish(featureName: string, properties: { [key: string]: string | number | boolean } = {}): void {
        this._telemetryService.publishEvent(TelemetryConstants.Area, featureName, { ...properties, ...this._artifactInfo });
    }
}
